import type {
  LoginRequestDto,
  LoginResponseDto,
  UpdateProfileRequestDto,
} from 'shared/dtos/authentication.dto';
import type {
  DeleteUserResponseDto,
  GetUserResponseDto,
  GetUsersResponseDto,
  InviteUserRequestDto,
  InviteUserResponseDto,
  UserDto,
} from 'shared/dtos/users.dto';

import { navigation } from 'shared/utils/navigation';

import { ApiService } from './api.service';

export default class UserService extends ApiService {
  public login = async ({
    username,
    password,
  }: LoginRequestDto): Promise<LoginResponseDto> => {
    const requestBody = {
      password: password,
      username: username,
    };

    const response = await this.post('/auth/login', requestBody);

    if (!response.error) {
      this.setCredentials(response.accessToken, response.refreshToken);
    }

    return response;
  };

  public logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken) {
      this.post('/auth/logout', { refreshToken });
    }

    this.unsetCredentials();
    if (navigation.navigate) {
      navigation.navigate('/login');
    }
  };

  public resetPassword = async (email) => {
    await this.post('/auth/reset-password', {
      email,
    });
  };

  public updatePassword = async (resetPasswordToken) => {
    await this.post('/auth/update-password', {
      resetPasswordToken,
    });
  };

  public loadProfile = async (): Promise<UserDto> => {
    const response = await this.get('/users/current');
    return response;
  };

  public updateProfile = async ({
    email,
    password,
  }: UpdateProfileRequestDto): Promise<UserDto> => {
    const requestBody = {
      email,
      password,
    };
    const response = await this.put('/users/current', requestBody);
    return response;
  };

  public getUsers = async (page: number): Promise<GetUsersResponseDto> => {
    return this.get(`/users/?page=${page}`);
  };

  public getUser = async (id: number): Promise<GetUserResponseDto> => {
    return this.get(`/users/${id}`);
  };

  public inviteUser = async (
    formData: InviteUserRequestDto,
  ): Promise<InviteUserResponseDto> => {
    return this.post('/users/', {
      username: formData.username,
      email: formData.email,
      roles: formData.roles,
    });
  };

  public deleteUser = async (id: number): Promise<DeleteUserResponseDto> => {
    return this.remove(`/users/${id}`);
  };
}
